import React from 'react';
import { Segment, Header, Button, Icon, Grid, Input, Label } from 'semantic-ui-react';
import { cleanPhone, isValidPhoneFormat, mergeDddAndLocalPhoneDigits, renegociacaoLocalDigitsFromFormField, parseRenegociacaoNumeroFieldInput, toBrazilNationalPhoneDigits } from '../../services/utils/stringUtils';
import { defaultContractRules, type TeamContractRules } from '../../services/teams/teamService';

interface LinhaContrato {
  id: string;
  negociacao: 'Aditivo' | 'Renegociação';
  ddd: string;
  numero: string;
  ccid?: string;
  chipCcid?: string;
  tipoVenda: string;
  planId: string;
  plano: string;
  aparelho?: string;
  valorChip: number;
  valorMensal: number;
  quantidade: number;
  seraUsadaPeloCliente?: boolean | null;
  excluirDoContrato?: boolean;
  requerSuspensao?: boolean;
  trocaLinha?: boolean;
  sourceContractId?: string;
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
}

interface LinesListProps {
  linhas: LinhaContrato[];
  onUpdateLinha: (id: string, field: string, value: string | number | boolean | null) => void;
  onRemoveLinha: (id: string) => void;
  onRemoveGroup: (planId: string, negociacao: string) => void;
  fastchip: boolean;
  hasDuplicateCcid: (linhas: any[]) => boolean;
  hasDuplicatePhone: (linhas: any[]) => boolean;
  getDuplicateCcidLines: (linhas: any[]) => string[];
  getDuplicatePhoneLines: (linhas: any[]) => string[];
  teamContractRules?: TeamContractRules;
  /** Quando false, oculta ações de criar/editar/excluir linhas. */
  allowLineMutations?: boolean;
}

const LinesList: React.FC<LinesListProps> = ({
  linhas,
  onUpdateLinha,
  onRemoveLinha,
  onRemoveGroup,
  fastchip,
  hasDuplicateCcid,
  hasDuplicatePhone,
  getDuplicateCcidLines,
  getDuplicatePhoneLines,
  teamContractRules = defaultContractRules,
  allowLineMutations = true,
}) => {
  const readOnly = !allowLineMutations;
  // Agrupar linhas por plano e tipo de negociação
  const groupedLines = linhas.reduce((acc, linha) => {
    const key = `${linha.planId}-${linha.negociacao}`;
    if (!acc[key]) {
      acc[key] = {
        plano: linha.plano ?? (linha as any).planName ?? '',
        negociacao: linha.negociacao,
        linhas: []
      };
    }
    acc[key].linhas.push(linha);
    return acc;
  }, {} as Record<string, { plano: string; negociacao: string; linhas: LinhaContrato[] }>);

  const handleNumeroChange = (id: string, value: string) => {
    const linhaAtual = linhas.find(l => l.id === id);
    const update = parseRenegociacaoNumeroFieldInput(linhaAtual?.ddd || '', value);
    if (update.ddd !== undefined) {
      onUpdateLinha(id, 'ddd', update.ddd);
    }
    onUpdateLinha(id, 'numero', update.numero);
  };

  const isNumeroValid = (numero: string) => {
    return numero && isValidPhoneFormat(numero);
  };

  const isNumeroDuplicado = (numero: string, currentId: string) => {
    if (!numero) return false;
    return linhas.some(linha => {
      if (linha.id === currentId || linha.negociacao !== 'Renegociação') return false;
      
      const numeroCompletoLinha = linha.ddd && linha.numero
        ? mergeDddAndLocalPhoneDigits(linha.ddd, linha.numero)
        : cleanPhone(linha.numero || '');
      
      return numeroCompletoLinha === numero;
    });
  };

  const hasCcidDuplicates = teamContractRules.blockDuplicateCcid && hasDuplicateCcid(linhas);
  const hasPhoneDuplicates = teamContractRules.blockDuplicatePhone && hasDuplicatePhone(linhas);
  const duplicateCcidLines = teamContractRules.blockDuplicateCcid ? getDuplicateCcidLines(linhas) : [];
  const duplicatePhoneLines = teamContractRules.blockDuplicatePhone ? getDuplicatePhoneLines(linhas) : [];

  return (
    <div>
      {hasCcidDuplicates && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          border: '1px solid #f44336', 
          borderRadius: '4px', 
          padding: '12px', 
          marginBottom: '15px',
          color: '#c62828'
        }}>
          <Icon name="warning" />
          <strong>CCIDs duplicados detectados!</strong>{' '}
          {duplicateCcidLines.length > 0 && (
            <span>({duplicateCcidLines.length} linha{duplicateCcidLines.length > 1 ? 's' : ''} afetada{duplicateCcidLines.length > 1 ? 's' : ''}) </span>
          )}
          Corrija os CCIDs duplicados antes de prosseguir.
        </div>
      )}
      
      {hasPhoneDuplicates && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          border: '1px solid #f44336', 
          borderRadius: '4px', 
          padding: '12px', 
          marginBottom: '15px',
          color: '#c62828'
        }}>
          <Icon name="warning" />
          <strong>Números de telefone duplicados detectados!</strong> Corrija os números duplicados antes de prosseguir.
        </div>
      )}

      {Object.entries(groupedLines).map(([key, group]) => (
        <Segment key={key} style={{ marginBottom: '15px' }}>
          <Header as="h4" style={{ marginBottom: '15px' }}>
            <Icon name="mobile" />
            {group.plano || (group.linhas[0] as any)?.planName || '-'} - {group.negociacao} ({group.linhas.length} linha{group.linhas.length > 1 ? 's' : ''})
            {allowLineMutations && (
            <Button
              size="small"
              color="red"
              floated="right"
              onClick={() => onRemoveGroup(group.linhas[0].planId, group.negociacao)}
              style={{ marginLeft: '10px' }}
            >
              <Icon name="trash" />
              Remover Todas
            </Button>
            )}
          </Header>

          <Grid>
            {group.linhas.map((linha, index) => (
              <Grid.Row key={linha.id} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <Grid.Column width={2}>
                  <strong>Linha {index + 1}</strong>
                  {linha.negociacao === 'Renegociação' && (linha.requerSuspensao || linha.excluirDoContrato) && (
                    <div style={{ marginTop: '4px' }}>
                      <Label color="red" size="mini">
                        <Icon name="ban" style={{ marginRight: '3px' }} />
                        Para suspender
                      </Label>
                    </div>
                  )}
                  {linha.negociacao === 'Aditivo' && linha.ehPortabilidade && (
                    <div style={{ marginTop: '4px' }}>
                      <Label color="blue" size="mini" title={`Portante: ${linha.portabilidadeNomeCompleto || ''} | CPF: ${linha.portabilidadeCpf || ''} | Op: ${linha.portabilidadeOperadora || ''} | Linha: ${linha.portabilidadeLinha || ''}`}>
                        <Icon name="exchange" style={{ marginRight: '3px' }} />
                        Portabilidade
                      </Label>
                    </div>
                  )}
                </Grid.Column>
                
                {linha.negociacao === 'Aditivo' && (
                  <Grid.Column width={3}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>DDD</label>
                      <Input
                        size="small"
                        value={linha.ddd}
                        readOnly={readOnly}
                        onChange={(e) => !readOnly && onUpdateLinha(linha.id, 'ddd', e.target.value.replace(/\D/g, ''))}
                        maxLength={2}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Grid.Column>
                )}

                {linha.negociacao === 'Aditivo' && (
                  <Grid.Column width={4}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>CCID {fastchip ? '*' : ''}</label>
                      {!fastchip ? (
                        <div
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            fontSize: '12px',
                            cursor: 'not-allowed',
                          }}
                        >
                          Não necessário (sem Fastchip)
                        </div>
                      ) : (
                        <div
                          title={linha.chipCcid || ''}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            backgroundColor: '#d4edda',
                            color: '#155724',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            cursor: 'not-allowed',
                          }}
                        >
                          <Icon name="lock" style={{ marginRight: '6px' }} />
                          {linha.chipCcid ? linha.chipCcid : 'CCID será preenchido automaticamente'}
                        </div>
                      )}
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                        {fastchip
                          ? 'Campo bloqueado: o sistema gerencia o CCID automaticamente.'
                          : 'CCID não é utilizado neste fluxo sem Fastchip.'}
                      </div>
                    </div>
                  </Grid.Column>
                )}

                {linha.negociacao === 'Aditivo' && linha.ehPortabilidade && (
                  <Grid.Column width={3}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#e53935', fontWeight: 'bold' }}>Linha a Portar *</label>
                      <Input
                        size="small"
                        placeholder="DDD + número"
                        value={linha.portabilidadeLinha || ''}
                        onChange={(e) => onUpdateLinha(linha.id, 'portabilidadeLinha', toBrazilNationalPhoneDigits(e.target.value))}
                        style={{ width: '100%' }}
                      />
                      {!linha.portabilidadeLinha && (
                        <div style={{ fontSize: '10px', color: '#ff6b6b', marginTop: '2px' }}>
                          Obrigatório para portabilidade
                        </div>
                      )}
                    </div>
                  </Grid.Column>
                )}

                {linha.negociacao === 'Renegociação' && (
                  <Grid.Column width={2}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>DDD</label>
                      <Input
                        size="small"
                        value={linha.ddd || ''}
                        readOnly={readOnly}
                        onChange={(e) => !readOnly && onUpdateLinha(linha.id, 'ddd', e.target.value.replace(/\D/g, '').slice(0, 2))}
                        maxLength={2}
                        placeholder="11"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Grid.Column>
                )}

                {linha.negociacao === 'Renegociação' && (
                  <Grid.Column width={4}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Número *</label>
                      <Input
                        size="small"
                        placeholder="999999999"
                        value={renegociacaoLocalDigitsFromFormField(linha.ddd || '', linha.numero || '')}
                        onChange={(e) => handleNumeroChange(linha.id, e.target.value)}
                        style={{ 
                          width: '100%',
                          borderColor: (linha.numero && teamContractRules.blockDuplicatePhone && (!isNumeroValid(linha.ddd && linha.numero ? mergeDddAndLocalPhoneDigits(linha.ddd, linha.numero) : linha.numero) || isNumeroDuplicado(linha.ddd && linha.numero ? mergeDddAndLocalPhoneDigits(linha.ddd, linha.numero) : linha.numero, linha.id) || duplicatePhoneLines.includes(linha.id))) ? '#ff6b6b' : '#ccc'
                        }}
                      />
                      {(() => {
                        const numeroCompleto = linha.ddd && linha.numero
                          ? mergeDddAndLocalPhoneDigits(linha.ddd, linha.numero)
                          : cleanPhone(linha.numero || '');
                        return numeroCompleto && !isNumeroValid(numeroCompleto);
                      })() && (
                        <div style={{ fontSize: '10px', color: '#ff6b6b', marginTop: '2px' }}>
                          Número deve ter 8 ou 9 dígitos (sem DDD)
                        </div>
                      )}
                      {(() => {
                        const numeroCompleto = linha.ddd && linha.numero
                          ? mergeDddAndLocalPhoneDigits(linha.ddd, linha.numero)
                          : cleanPhone(linha.numero || '');
                        return (
                          teamContractRules.blockDuplicatePhone &&
                          numeroCompleto &&
                          (isNumeroDuplicado(numeroCompleto, linha.id) || duplicatePhoneLines.includes(linha.id))
                        );
                      })() && (
                        <div style={{ fontSize: '10px', color: '#ff6b6b', marginTop: '2px' }}>
                          Número duplicado - já está sendo usado em outra linha
                        </div>
                      )}
                      {!linha.numero && (
                        <div style={{ fontSize: '10px', color: '#ff6b6b', marginTop: '2px' }}>
                          Número obrigatório para Renegociação
                        </div>
                      )}
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                        Apenas o número, sem DDD (até 9 dígitos)
                      </div>
                    </div>
                  </Grid.Column>
                )}

                <Grid.Column width={2}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#666' }}>Valor</label>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      R$ {(linha.valorMensal || 0).toFixed(2)}
                    </div>
                  </div>
                </Grid.Column>

                {linha.negociacao === 'Aditivo' && (
                  <Grid.Column width={3}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Usada pelo cliente?</label>
                      <div style={{
                        display: 'flex',
                        gap: '4px',
                        border: teamContractRules.requireSeraUsadaPeloCliente && linha.seraUsadaPeloCliente == null ? '2px solid #ffc107' : '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '4px',
                        backgroundColor: teamContractRules.requireSeraUsadaPeloCliente && linha.seraUsadaPeloCliente == null ? '#fff3cd' : '#fff'
                      }}>
                        <Button
                          type="button"
                          size="mini"
                          positive={linha.seraUsadaPeloCliente === true}
                          onClick={() => onUpdateLinha(linha.id, 'seraUsadaPeloCliente', true)}
                          style={{
                            flex: 1,
                            fontWeight: linha.seraUsadaPeloCliente === true ? 'bold' : 'normal',
                            fontSize: '12px',
                            padding: '6px 4px'
                          }}
                        >
                          Sim
                        </Button>
                        <Button
                          type="button"
                          size="mini"
                          negative={linha.seraUsadaPeloCliente === false}
                          onClick={() => onUpdateLinha(linha.id, 'seraUsadaPeloCliente', false)}
                          style={{
                            flex: 1,
                            fontWeight: linha.seraUsadaPeloCliente === false ? 'bold' : 'normal',
                            fontSize: '12px',
                            padding: '6px 4px'
                          }}
                        >
                          Não
                        </Button>
                      </div>
                      {teamContractRules.requireSeraUsadaPeloCliente && linha.seraUsadaPeloCliente == null && (
                        <div style={{ fontSize: '10px', color: '#e65100', marginTop: '2px', fontWeight: 'bold' }}>
                          Obrigatório
                        </div>
                      )}
                    </div>
                  </Grid.Column>
                )}

                {allowLineMutations && (
                <Grid.Column width={1}>
                  <Button
                    size="small"
                    color="red"
                    icon="trash"
                    onClick={() => onRemoveLinha(linha.id)}
                    style={{ marginTop: '20px' }}
                  />
                </Grid.Column>
                )}
              </Grid.Row>
            ))}
          </Grid>
        </Segment>
      ))}

      {linhas.length === 0 && (
        <Segment textAlign="center" style={{ padding: '40px' }}>
          <Icon name="mobile" size="large" color="grey" />
          <Header as="h4" color="grey">
            Nenhuma linha adicionada
          </Header>
          <p>Adicione linhas usando o formulário acima</p>
        </Segment>
      )}
    </div>
  );
};

export default LinesList;
